import React from 'react';
import { useConfig } from '../config/ConfigContext';

const SEOHead = ({ 
  title, 
  description, 
  image, 
  url, 
  canonical,
  noindex = false,
  type = 'website',
  additionalMeta = [] 
}) => {
  const { config } = useConfig();

  React.useEffect(() => {
    if (!config || !config.seo || !config.branding) return;

    const pageTitle = title 
      ? `${title} - ${config.branding.companyName}`
      : (config.seo.siteTitle || 'Home').replace('{companyName}', config.branding.companyName || '');
    
    const pageDescription = description || config.seo.siteDescription || '';
    const pageImage = image || config.seo.ogImage || '';
    const currentUrl = url || window.location.href;
    const canonicalUrl = canonical || currentUrl.split('?')[0]; // Remove query params by default

    // Update title
    document.title = pageTitle;

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonicalUrl;
      document.head.appendChild(canonicalLink);
    }

    // Update or create meta tags
    const metaTags = [
      // Basic SEO
      { name: 'description', content: pageDescription },
      { name: 'keywords', content: config.seo.keywords },
      
      // Robots
      ...(noindex ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),
      
      // Open Graph (Facebook, LinkedIn)
      { property: 'og:title', content: pageTitle },
      { property: 'og:description', content: pageDescription },
      { property: 'og:image', content: pageImage },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: config.branding.companyName },
      { property: 'og:locale', content: 'en_US' },
      
      // Twitter Card
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle },
      { name: 'twitter:description', content: pageDescription },
      { name: 'twitter:image', content: pageImage },
      
      // Mobile Web App
      { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=5' },
      { name: 'theme-color', content: '#2563eb' }, // Blue-600
      { name: 'mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
      { name: 'apple-mobile-web-app-title', content: config.branding.companyName },
      
      // Additional custom meta
      ...additionalMeta
    ];

    metaTags.forEach(({ name, property, content }) => {
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let meta = document.querySelector(selector);
      
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        if (name) meta.name = name;
        if (property) meta.property = property;
        meta.content = content;
        document.head.appendChild(meta);
      }
    });

    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Store",
      "name": config.branding.companyName,
      "description": config.branding.description,
      "url": currentUrl,
      "telephone": config.branding.contactPhone,
      "email": config.branding.contactEmail
    };

    let jsonLd = document.querySelector('#structured-data');
    if (jsonLd) {
      jsonLd.textContent = JSON.stringify(structuredData);
    } else {
      jsonLd = document.createElement('script');
      jsonLd.id = 'structured-data';
      jsonLd.type = 'application/ld+json';
      jsonLd.textContent = JSON.stringify(structuredData);
      document.head.appendChild(jsonLd);
    }

  }, [config, title, description, image, url, canonical, noindex, type, additionalMeta]);

  return null; // This component only manages head elements
};

export default SEOHead;